import type { DecisionStatus } from './enums'

/**
 * Tipos de decisão (`supply.q3_decision.tipo`, migration 017):
 *   executar — tem responsável e prazo (opcionais na criação) e status;
 *   registro — "bater o martelo": só documentada, sem responsável nem prazo.
 *              `aberta` = vigente, `descartada` = revogada.
 */
export const DECISION_TIPOS = ['executar', 'registro'] as const
export type DecisionTipo = (typeof DECISION_TIPOS)[number]

export const DECISION_TIPO_LABEL: Record<DecisionTipo, string> = {
  executar: 'A executar',
  registro: 'Registro',
}

export const isDecisionTipo = (v: unknown): v is DecisionTipo => (DECISION_TIPOS as readonly unknown[]).includes(v)

/** Status escolhíveis à mão por tipo — "virou tarefa/ação" só vem da conversão. */
export const DECISION_MANUAL_STATUSES: Record<DecisionTipo, readonly DecisionStatus[]> = {
  executar: ['aberta', 'concluida', 'descartada'],
  registro: ['aberta', 'descartada'],
}

const REGISTRO_STATUS_LABEL: Partial<Record<DecisionStatus, string>> = { aberta: 'Vigente', descartada: 'Revogada' }

/** Rótulo do status conforme o tipo (registro: Vigente/Revogada). */
export function decisionStatusLabel(tipo: DecisionTipo, status: DecisionStatus, fallback: Record<DecisionStatus, string>): string {
  return (tipo === 'registro' ? REGISTRO_STATUS_LABEL[status] : undefined) ?? fallback[status]
}

export interface DecisionPendencyInput {
  readonly tipo: DecisionTipo
  readonly status: DecisionStatus
  readonly prazo: string | null
  readonly owner_id: string | null
  readonly created_by: string | null
}

/**
 * "Decisão sem prazo": A executar, aberta e sem prazo. É pendência do
 * responsável e de quem registrou até ganhar prazo ou sair de aberta.
 * Mesma régua do aviso inicial `decisao_sem_prazo` (gatilho da migration 017).
 */
export function isDecisionWithoutDeadline(d: DecisionPendencyInput, memberId?: string): boolean {
  if (d.tipo !== 'executar' || d.status !== 'aberta' || d.prazo) return false
  return memberId === undefined || d.owner_id === memberId || d.created_by === memberId
}

/** Minúsculas e sem acento — para comparar termo de busca com texto. */
export const foldText = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

/**
 * Trechos do texto com os termos da busca marcados (`hit`). Aproxima o
 * dicionário `portuguese` do banco: sem acento, sem caixa, e casa a palavra
 * pelo começo (radical = termo sem as 2 últimas letras, mínimo 3), então
 * "parar" marca "Parar" e "parou", "cotação" marca "cotacoes".
 */
export function highlightParts(text: string, query: string): { text: string; hit: boolean }[] {
  const stems = [...new Set(
    foldText(query)
      .split(/[^\p{L}\p{N}]+/u)
      .filter((t) => t.length >= 2 && !['or', 'and', 'ou', 'e'].includes(t))
      .map((t) => (t.length > 4 ? t.slice(0, Math.max(3, t.length - 2)) : t)),
  )]
  if (!stems.length || !text) return [{ text, hit: false }]
  const out: { text: string; hit: boolean }[] = []
  for (const piece of text.split(/([\p{L}\p{N}]+)/u)) {
    if (!piece) continue
    const hit = /[\p{L}\p{N}]/u.test(piece) && stems.some((s) => foldText(piece).startsWith(s))
    const last = out[out.length - 1]
    if (last && last.hit === hit) last.text += piece
    else out.push({ text: piece, hit })
  }
  return out
}
